import React, {
  useContext,
  useState
} from 'react';
import ReactMarkdown from 'react-markdown';
import { ELSButton } from '@els/els-component-button-react';
import {
  ELSButtonIconPosition,
  ELSButtonSize,
  ELSButtonType
} from '../../models/button.models';
import { FlashcardDto } from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { ELSProgressBar } from '../els.components';
import { FlexLayout } from '../flex/FlexLayout.component';
import { FlexItem } from '../flex/FlexItem.component';
import { FlexLayoutModifier } from '../flex/flex.constants';
import { AppContext } from '../app/AppProvider.component';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import { trackNewRelicAction } from '../app/app-state-actions.utilities';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import {
  copyContentToClipboard,
  formatFlashcardsForCopy,
  MARKDOWN_REMARK_PLUGINS,
  MARKDOWN_REHYPE_PLUGINS
} from '../../utilities/common.utilities';
import { BASE_TOAST_CONFIG } from '../../constants/toast.constants';
import IconWithText from '../icon-with-text/IconWithText.component';

export type FlashcardPlayerPropsOnly = {
  flashcards: FlashcardDto[];
};

export type FlashcardPlayerProps = FlashcardPlayerPropsOnly & ELSPropsFromToastService;

enum FlashcardSide {
  FRONT = 'FRONT',
  BACK = 'BACK'
}

export type FlashcardPlayerState = {
  activeIndex: number;
  activeSide: FlashcardSide;
  preferredSide: FlashcardSide;
};

export const FlashcardPlayerComponent = (props: FlashcardPlayerProps): JSX.Element => {
  const { flashcards } = props;

  const {
    appState,
    appStateRef
  } = useContext(AppContext);

  const [state, setState] = useState<FlashcardPlayerState>({
    activeIndex: 0,
    activeSide: FlashcardSide.FRONT,
    preferredSide: FlashcardSide.FRONT
  });

  if (!flashcards || flashcards.length === 0) {
    return <div className="c-naa-flashcard-player__empty">No flashcards available</div>;
  }

  const currentCard = flashcards[state.activeIndex];
  const totalCards = flashcards.length;

  const handlePrevious = () => {
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_FLASHCARD_PLAYER_CLICK,
        props: {
          btnName: 'previous',
          chatUserRole: appState.chatUserRole
        }
      }
    });

    setState((prevState) => ({
      ...prevState,
      activeIndex: prevState.activeIndex === 0 ? totalCards - 1 : prevState.activeIndex - 1,
      activeSide: prevState.preferredSide
    }));
  };

  const handleNext = () => {
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_FLASHCARD_PLAYER_CLICK,
        props: {
          btnName: 'next',
          chatUserRole: appState.chatUserRole
        }
      }
    });

    setState((prevState) => ({
      ...prevState,
      activeIndex: prevState.activeIndex === totalCards - 1 ? 0 : prevState.activeIndex + 1,
      activeSide: prevState.preferredSide
    }));
  };

  const handleFlip = () => {
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_FLASHCARD_PLAYER_CLICK,
        props: {
          btnName: 'flip',
          chatUserRole: appState.chatUserRole
        }
      }
    });

    setState((prevState) => ({
      ...prevState,
      activeSide: prevState.activeSide === FlashcardSide.FRONT ? FlashcardSide.BACK : FlashcardSide.FRONT
    }));
  };

  const handleFirstSideChange = () => {
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_FLASHCARD_PLAYER_CLICK,
        props: {
          btnName: 'toggle_preferred_side',
          chatUserRole: appState.chatUserRole
        }
      }
    });

    setState((prevState) => ({
      ...prevState,
      preferredSide: prevState.preferredSide === FlashcardSide.FRONT ? FlashcardSide.BACK : FlashcardSide.FRONT,
      activeSide: prevState.preferredSide === FlashcardSide.FRONT ? FlashcardSide.BACK : FlashcardSide.FRONT
    }));
  };

  const handleCopyClick = () => {
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_FLASHCARD_COPY_CLICK,
        props: {
          chatUserRole: appState.chatUserRole
        }
      }
    });

    const formattedFlashcards = formatFlashcardsForCopy(flashcards);

    copyContentToClipboard(formattedFlashcards);

    props.toastService.openToast({
      ...BASE_TOAST_CONFIG,
      component: (
        <div>Successfully copied all flashcards</div>
      ),
      type: ELSWithToastService.types.POSITIVE
    });
  };

  return (
    <div className="c-naa-flashcard-player" role="region" aria-label="Flashcard">
      <div className="u-els-margin-bottom">
        <h3>Flashcards</h3>
      </div>

      <div className="c-naa-flashcard-player__controls">

        <FlexLayout modifiers={[
          FlexLayoutModifier.GUTTERS,
          FlexLayoutModifier.MIDDLE,
        ]}>
          <FlexItem>
            <ELSButton
              type={ELSButtonType.SECONDARY}
              size={ELSButtonSize.SMALL}
              onClick={handlePrevious}
              preventAutoPending
            >
              Previous
            </ELSButton>
          </FlexItem>
          <FlexItem modifiers={[
            FlexLayoutModifier.GROW
          ]}>
            <button
              type="button"
              className="u-els-anchorize"
              onClick={handleFirstSideChange}
            >
              {state.preferredSide === FlashcardSide.FRONT ? 'Showing front first' : 'Showing back first'}
            </button>
          </FlexItem>
          <FlexItem>
            <ELSButton
              type={ELSButtonType.SECONDARY}
              size={ELSButtonSize.SMALL}
              onClick={handleNext}
              preventAutoPending
            >
              Next
            </ELSButton>
          </FlexItem>
        </FlexLayout>
      </div>

      <div className="o-els-container">
        <ELSProgressBar
          type="secondary"
          progressNum={state.activeIndex + 1}
          totalNum={totalCards}
        />
      </div>

      <div className="c-naa-flashcard-player__content">
        <button
          onClick={handleFlip}
          type="button"
          className={`c-naa-flashcard-player__card${state.activeSide === FlashcardSide.BACK ? ' c-naa-flashcard-player__card--back' : ''}`}
        >
          <div className="c-naa-flashcard-player__card-header">
            <FlexLayout modifiers={[
              FlexLayoutModifier.GUTTERS
            ]}>
              <FlexItem modifiers={[
                FlexLayoutModifier.GROW
              ]}>
                <span className="c-naa-flashcard-player__counter">
                  {state.activeIndex + 1} / {totalCards}
                </span>
              </FlexItem>
              <FlexItem>
                <span className="c-naa-flashcard-player__card-label">
                  {state.activeSide === FlashcardSide.FRONT ? 'Front' : 'Back'}
                </span>
              </FlexItem>
            </FlexLayout>
          </div>
          <div className="c-naa-flashcard-player__card-text">
            <div>
              <ReactMarkdown
                remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                rehypePlugins={MARKDOWN_REHYPE_PLUGINS}
              >
                {state.activeSide === FlashcardSide.FRONT ? currentCard.frontText : currentCard.backText}
              </ReactMarkdown>
            </div>
          </div>
          <div className="c-naa-flashcard-player__card-footer">
            <span className="u-els-font-size-meta u-els-font-family-italic">
              {state.activeSide === FlashcardSide.FRONT ? 'Click to view back' : 'Click to view front'}
            </span>
          </div>
        </button>
      </div>

      <div className="u-els-margin-top">
        <FlexLayout modifiers={[
          FlexLayoutModifier.GUTTERS,
          FlexLayoutModifier.MIDDLE,
          FlexLayoutModifier.LEFT,
        ]}>
          <FlexItem>
            {/*
              Matches the quiz panel's copy control: the label is visible rather
              than screen-reader-only, and is the button's only accessible name.
            */}
            <button
              onClick={handleCopyClick}
              type="button"
              className="u-els-debuttonize u-els-color-secondary u-els-display-block u-els-margin-top-1o8"
            >
              <IconWithText
                iconName="duplicate-document"
                iconPrefix="gizmo"
                iconPosition={ELSButtonIconPosition.LEFT}
              >
                Copy all flashcards
              </IconWithText>
            </button>
          </FlexItem>
        </FlexLayout>
      </div>
    </div>
  );
};

export const FlashcardPlayer: React.ComponentType<FlashcardPlayerPropsOnly> = ELSWithToastService(FlashcardPlayerComponent);

export default FlashcardPlayer;
